import { useEffect, useState } from "react"
import {
  Box,
  Chip,
  CircularProgress,
  Link,
  Tooltip,
  Typography,
} from "@mui/material"
import { CampusClient } from "@clients/campusClient"
import { formatProgramName } from "./programLabel"

type AttendanceTotals = {
  present: number
  absentExcused: number
  absentUnexcused: number
  pending: number
  noData: number
  expectedDays: number
}

type ProgramAttendanceSummary = {
  offeringClientId: string
  // The program's real name (campus title, e.g. "IHK Data Analytics"), so the
  // breakdown labels each class by program — optional: older campus responses
  // predate it, and the label falls back to the school + cohort.
  programTitle?: string
  domain: string | null
  cohortStartDate: string | null
  className: string
  totals: AttendanceTotals
  attendanceRate: number | null
}

// Pooled across every active offering (a student is one person), with the
// per-program breakdown carried alongside.
type AggregateAttendanceSummary = {
  totals: AttendanceTotals
  attendanceRate: number | null
  programs: ProgramAttendanceSummary[]
}

// Same fade-at-the-cut scroll affordance as the sibling tiles.
const tileScrollSx = {
  overflowY: "auto",
  scrollbarWidth: "thin",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: "3px",
  },
  background: `
    linear-gradient(#fafaf7 30%, rgba(250, 250, 247, 0)) center top,
    linear-gradient(rgba(250, 250, 247, 0), #fafaf7 70%) center bottom,
    radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) center top,
    radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) center bottom`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "100% 32px, 100% 32px, 100% 12px, 100% 12px",
  backgroundAttachment: "local, local, scroll, scroll",
} as const

// Lead with the program's real name + cohort (the add-picker label), falling
// back to the class marketing name only when neither name nor cohort is known.
const programName = (program: ProgramAttendanceSummary): string =>
  program.cohortStartDate
    ? formatProgramName({
        name: program.programTitle,
        domain: program.domain ?? "",
        startDate: program.cohortStartDate,
      })
    : program.programTitle || program.className

const hasProgramRecords = (program: ProgramAttendanceSummary): boolean =>
  program.attendanceRate !== null ||
  program.totals.pending > 0 ||
  program.totals.noData > 0

// A labeled quick look into the attendance feature, pooled across all the
// student's active offerings — the headline number is their real engagement
// everywhere, with a per-program breakdown and one "Open attendance" per class.
// Attendance still lives in attendance; this is a look, not a second surface.
export function StudentAttendanceQuickLook(props: { studentId: string }) {
  const { studentId } = props
  const [summary, setSummary] = useState<AggregateAttendanceSummary | null>(
    null,
  )
  const [error, setError] = useState(false)

  useEffect(() => {
    setSummary(null)
    setError(false)
    CampusClient.get<AggregateAttendanceSummary>(
      `attendance-tracking/status/student/${studentId}/summary-all`,
    )
      .then(setSummary)
      .catch(() => setError(true))
  }, [studentId])

  if (error) {
    // The viewer may lack the attendance roles; the panel stays clean.
    return null
  }

  const attendancePageUrl = (program: ProgramAttendanceSummary) =>
    `${
      import.meta.env.VITE_CAMPUS_URL
    }/dashboard/attendance-tracking/student/${studentId}?class=${
      program.offeringClientId
    }&cn=${encodeURIComponent(program.className)}`

  const hasRecords =
    summary !== null &&
    (summary.attendanceRate !== null ||
      summary.totals.pending > 0 ||
      summary.totals.noData > 0)

  return (
    <Box
      sx={{
        px: "16px",
        py: "12px",
        borderBottom: "1px solid #eee",
        flex: "1 1 auto",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Same stretch-or-center treatment as the sibling tiles, so the data
          tiles align consistently at every width. */}
      <Box
        sx={{
          maxWidth: 720,
          mx: "auto",
          width: "100%",
          flex: "1 1 auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px",
            mb: "8px",
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Attendance
          </Typography>
          {summary && summary.attendanceRate !== null && (
            <Chip
              size="small"
              color={summary.attendanceRate >= 80 ? "success" : "warning"}
              label={`${summary.attendanceRate}%`}
            />
          )}
          {summary && !hasRecords && (
            <Chip size="small" variant="outlined" label="No records yet" />
          )}
        </Box>
        {!summary && (
          <Box
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={18} />
          </Box>
        )}
        {summary && hasRecords && (
          <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <AttendanceStat label="Present" value={summary.totals.present} />
            <AttendanceStat
              label="Excused"
              value={summary.totals.absentExcused}
            />
            <AttendanceStat
              label="Unexcused"
              value={summary.totals.absentUnexcused}
              highlight={summary.totals.absentUnexcused > 0}
            />
            <AttendanceStat label="Pending" value={summary.totals.pending} />
          </Box>
        )}
        {/* Per-program breakdown: the pooled total above is the student's real
            number; this names each class it came from, with its own rate and an
            Open-attendance shortcut. Only this list scrolls. */}
        {summary && summary.programs.length > 0 && (
          <Box
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              mt: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              ...tileScrollSx,
            }}
          >
            {summary.programs.map((program) => (
              <Box
                key={program.offeringClientId}
                sx={{ opacity: hasProgramRecords(program) ? 1 : 0.55 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  <Tooltip title={programName(program)}>
                    <Typography variant="body3_sb" noWrap sx={{ minWidth: 0 }}>
                      {programName(program)}
                    </Typography>
                  </Tooltip>
                  {program.attendanceRate !== null ? (
                    <Chip
                      size="small"
                      color={
                        program.attendanceRate >= 80 ? "success" : "warning"
                      }
                      label={`${program.attendanceRate}%`}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      no records
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    mt: "2px",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {program.totals.present} present ·{" "}
                    {program.totals.absentExcused} excused ·{" "}
                    {program.totals.absentUnexcused} unexcused
                  </Typography>
                  {/* Not impersonation — the page it opens is the staff
                      dashboard; you stay signed in as you. */}
                  <Tooltip title="Open the staff attendance view for this class">
                    <Link
                      component="button"
                      variant="caption"
                      onClick={() =>
                        window.open(
                          attendancePageUrl(program),
                          "_blank",
                          "noopener",
                        )
                      }
                      sx={{ flexShrink: 0 }}
                    >
                      Open ↗
                    </Link>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}

function AttendanceStat(props: {
  label: string
  value: number
  highlight?: boolean
}) {
  return (
    <Box>
      <Typography
        variant="body2_sb"
        color={props.highlight ? "error" : "text.primary"}
      >
        {props.value}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {props.label}
      </Typography>
    </Box>
  )
}
